import type { AccessRuleInputData } from '@sunstone/api-client/hooks';
import { Button, Card, CardContent } from '@sunstone/ui';
import { ChevronRight, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { RuleFields } from './AccessRuleFields';
import {
  emptyRuleDraft,
  type ResourceKind,
  type RuleDraft,
  ruleDraftConditionJson,
  ruleDraftHasContent,
} from './accessRuleDraft';
import { ruleSentence, useResourceNames } from './accessRuleSentence';
import { DeploymentPicker } from './DeploymentPicker';
import { WorkspacePicker } from './WorkspacePicker';

interface AccessRuleListEditorProps {
  tenantId: string;
  /** Scopes the picker to a partner's descendant tenants instead of
   * tenantId — a partner owns no deployments/workspaces directly, only its
   * child tenants do. Only affects the picker/preview calls; resource-name
   * resolution (useResourceNames below) still uses tenantId as-is. */
  parentTenantId?: string;
  resourceKind: ResourceKind;
  drafts: RuleDraft[];
  onChange: (drafts: RuleDraft[]) => void;
  allowEffectToggle: boolean;
  /** The outer (role) and inner (user) access-rule envelopes to scope this
   * list's picker to (accessrule.gateBoth) — forwarded verbatim to whichever
   * picker this list renders (DeploymentPicker or WorkspacePicker). */
  roleRules?: AccessRuleInputData[];
  userRules?: AccessRuleInputData[];
}

/**
 * List of independent access-rule drafts, all of one resource kind (SUN-408
 * round 3: deployment-kind and workspace-kind rules are two separate lists —
 * not paired units). The UI caps each list at one rule; the backend still
 * allows more per kind, this only limits what the editor offers to add. A
 * row starts collapsed to a one-line summary once it has content; only a
 * freshly-added blank row auto-expands. One instance renders per kind — the
 * page owns which kind's `drafts` it's passed and wires the
 * eligible-deployments hookup between the two.
 */
export function AccessRuleListEditor({
  tenantId,
  parentTenantId,
  resourceKind,
  drafts,
  onChange,
  allowEffectToggle,
  roleRules,
  userRules,
}: AccessRuleListEditorProps) {
  const resourceName = useResourceNames(tenantId);
  // parentTenantId and tenantId are mutually exclusive on the wire — when
  // scoping to a partner, the picker/preview's own tenantId must be empty
  // so the backend dispatches on parent_tenant_id instead.
  const pickerTenantId = parentTenantId ? '' : tenantId;
  const [expandedKey, setExpandedKey] = useState<string | null>(() => {
    const only = drafts.length === 1 ? drafts[0] : undefined;
    return only && !ruleDraftHasContent(only) ? only.draftKey : null;
  });
  const [searchByRow, setSearchByRow] = useState<Record<string, string>>({});

  function updateDraft(index: number, draft: RuleDraft) {
    onChange(drafts.map((d, i) => (i === index ? draft : d)));
  }

  function removeDraft(index: number) {
    const removed = drafts[index];
    onChange(drafts.filter((_, i) => i !== index));
    if (removed?.draftKey === expandedKey) setExpandedKey(null);
  }

  function addDraft() {
    const draft = emptyRuleDraft('allow', resourceKind);
    onChange([...drafts, draft]);
    setExpandedKey(draft.draftKey);
  }

  return (
    <div className="space-y-3">
      {drafts.map((draft, index) => {
        const expanded = draft.draftKey === expandedKey;
        return (
          <Card key={draft.draftKey}>
            {expanded ? (
              <CardContent className="pt-6 space-y-3">
                <div className="flex items-center justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Remove this rule"
                    onClick={() => removeDraft(index)}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <RuleFields
                  tenantId={pickerTenantId}
                  parentTenantId={parentTenantId}
                  draft={draft}
                  onChange={(next) => updateDraft(index, next)}
                  allowEffectToggle={allowEffectToggle}
                  roleRules={roleRules}
                  userRules={userRules}
                  picker={(d, onDraftChange) =>
                    resourceKind === 'deployment' ? (
                      <DeploymentPicker
                        tenantId={pickerTenantId}
                        parentTenantId={parentTenantId}
                        value={d.resourceIds}
                        onChange={(ids) => onDraftChange({ ...d, resourceIds: ids })}
                        search={searchByRow[d.draftKey] ?? ''}
                        onSearchChange={(v) =>
                          setSearchByRow((prev) => ({ ...prev, [d.draftKey]: v }))
                        }
                        roleRules={roleRules}
                        userRules={userRules}
                      />
                    ) : (
                      <WorkspacePicker
                        tenantId={pickerTenantId}
                        parentTenantId={parentTenantId}
                        value={d.resourceIds}
                        onChange={(ids) => onDraftChange({ ...d, resourceIds: ids })}
                        search={searchByRow[d.draftKey] ?? ''}
                        onSearchChange={(v) =>
                          setSearchByRow((prev) => ({ ...prev, [d.draftKey]: v }))
                        }
                        roleRules={roleRules}
                        userRules={userRules}
                      />
                    )
                  }
                />
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpandedKey(null)}
                  >
                    Collapse
                  </Button>
                </div>
              </CardContent>
            ) : (
              <button
                type="button"
                onClick={() => setExpandedKey(draft.draftKey)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
              >
                <p className="text-sm text-foreground flex-1">
                  {ruleDraftHasContent(draft)
                    ? ruleSentence(
                        {
                          effect: draft.effect,
                          resourceIds: draft.resourceIds,
                          restrictionKind: draft.restrictionKind,
                          ruleConditionJson: ruleDraftConditionJson(draft),
                        },
                        resourceName,
                      )
                    : 'Not yet configured'}
                </p>
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              </button>
            )}
          </Card>
        );
      })}
      {drafts.length === 0 && (
        <Button type="button" variant="outline" size="sm" onClick={addDraft}>
          <Plus className="h-4 w-4" />
          Add {resourceKind} rule
        </Button>
      )}
    </div>
  );
}
